import ActivityEvent from '@/models/activityEvent';
import { ActivityEventType, EnumObject, PipelinePhaseType, PipelineStatus } from '@/models/enums';
import Job from '@/models/job';
import _Vue from 'vue';

export function Utilities(Vue: typeof _Vue): void {

  Vue.prototype.arraysAreEqual = function <T>(array1: T[], array2: T[]): boolean {
    let equal = true;
    equal = equal && array1.length === array2.length;
    if (equal) {
      for (let i = 0; i < array1.length; i++) {
        if (typeof array1[i] === 'object') {
          equal = equal && this.objectsAreEqual(array1[i], array2[i]);
        } else {
          equal = equal && array1[i] === array2[i];
        }
      }
    }
    return equal;
  };

  Vue.prototype.dateRangeTextUtility = function (dateRange: string[]): string {
    const dateString = dateRange
      .map((x) => {
        const date = new Date(x + 'T12:00:00Z');
        return date.toLocaleDateString();
      })
      .join('~');
    return dateString;
  };

  Vue.prototype.deepClone = function <T>(inObject: T | T[]): T | T[] {
    let outObject, value;

    if (typeof inObject !== 'object' || inObject === null) {
      return inObject; // Return the value if inObject is not an object
    }

    // Create an array or object to hold the values
    outObject = Array.isArray(inObject) ? ([] as T[]) : ({} as T);
    if (Array.isArray(inObject)) {
      outObject = [] as T[];
      for (let x = 0; x < inObject.length; x++) {
        value = inObject[x];
        outObject[x] = this.deepClone(value);
      }
    } else {
      outObject = {} as T;
      let k: keyof T;
      for (k in inObject) {
        value = inObject[k];
        outObject[k] = this.deepClone(value);
      }
    }

    return outObject;
  };

  Vue.prototype.enumToArray = function (enumToProcess: Record<string, string>): EnumObject[] {
    const enumArray = [] as EnumObject[];
    let k: keyof Record<string, string>;
    for (k in enumToProcess) {
      const keyVal = parseInt(k);
      if (!isNaN(keyVal)) {
        const val = enumToProcess[k].toString();
        enumArray.push(new EnumObject(keyVal, val));
      }
    }
    return enumArray;
  };

  Vue.prototype.getEventsStatus = function (events: Array<ActivityEvent>): number {
    const allStarted = events.filter((x) => x.activityEventTypeId === ActivityEventType.Started);
    const allCompleted = events.filter((x) => x.activityEventTypeId === ActivityEventType.Completed);
    // bim360 polling is activityDefinitionId 33 - it can have multiple started events per completed event
    // cp 10/21/20
    const allStartsCompleted = events.every((x) => x.activityDefinitionId === 33)
      ? allCompleted.length > 0
      : allCompleted.length >= allStarted.length;
    const lastEvent =
      allStartsCompleted && allCompleted.length > 0 ? allCompleted[allCompleted.length - 1] : events[events.length - 1];
    const lastEventPasses =
      lastEvent?.activityDefinitionId === 33 ||
      (lastEvent?.isSuccessful && lastEvent?.activityEventTypeId === ActivityEventType.Completed);
    const failed = events.every((x) => x.activityDefinitionId === 33 && allCompleted.length > 1)
      ? allCompleted.length > 0 && allCompleted.every((x) => !x.isSuccessful)
      : events.some((x) => !x.isSuccessful);
    return events.length === 0
      ? PipelineStatus.Pending
      : failed
      ? PipelineStatus.Failed
      : lastEventPasses && allStartsCompleted
      ? PipelineStatus.Success
      : PipelineStatus.Processing;
  };

  Vue.prototype.getIconColor = function (job: Job, phaseTypeId: PipelinePhaseType | null = null): string {
    let hasExceptions = !phaseTypeId || phaseTypeId === PipelinePhaseType.Extract ? job.extractExceptions > 0 : false;
    hasExceptions =
      hasExceptions ||
      (!phaseTypeId || phaseTypeId === PipelinePhaseType.Transform ? job.transformExceptions > 0 : false);
    hasExceptions =
      hasExceptions || (!phaseTypeId || phaseTypeId === PipelinePhaseType.Load ? job.loadExceptions > 0 : false);
    hasExceptions = hasExceptions || (phaseTypeId === null && job.telemetryErrorCount > 0);
    const isProcessing =
      PipelineStatus.Processing ===
      (phaseTypeId === PipelinePhaseType.Extract
        ? job.extractStatus
        : phaseTypeId === PipelinePhaseType.Transform
        ? job.transformStatus
        : job.loadStatus);
    return hasExceptions ? 'warning' : isProcessing ? 'primary' : 'success';
  };

  Vue.prototype.isJSON = function (str: string) {
    if (str) {
      if (/^\s*$/.test(str)) return false;
      str = str.replace(/\\(?:["\\/bfnrt]|u[0-9a-fA-F]{4})/g, '@');
      str = str.replace(/"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/g, ']');
      str = str.replace(/(?:^|:|,)(?:\s*\[)+/g, '');
      return /^[\],:{}\s]*$/.test(str);
    } else {
      return false;
    }
  };

  Vue.prototype.objectsAreEqual = function <T>(object1: T, object2: T): boolean {
    let k: keyof T;
    let same = true;
    for (k in object1) {
      same = same && object1[k] === object2[k];
    }
    return same;
  };

  Vue.prototype.sortArray = function <T, K extends keyof T>(array: Array<T>, sortParam: K, desc?: boolean): Array<T> {
    const sortedArray = array.sort((a: T, b: T) => {
      if (a[sortParam] < b[sortParam]) {
        return -1;
      } else if (a[sortParam] > b[sortParam]) {
        return 1;
      } else {
        return 0;
      }
    });
    return desc ? sortedArray.reverse() : sortedArray;
  };

  Vue.prototype.tryFormatJson = function (input: string) {
    if (!input) {
      return 'No Additional Information Available';
    } else if (this.isJSON(input)) {
      const obj = JSON.parse(input);
      const output = JSON.stringify(obj, undefined, 4);
      return output;
    } else return input;
  };
}
